import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import HomePage from "./components/Home/HomePage";
import LoginForm from "./components/Login/LoginForm";
import ForgotPasswordModal from "./components/Login/ForgotPasswordModal";
import Service from "./components/serviceOverview/Service";
import NotFound from "./components/NotFound";
import VerifyOTP from "./components/Login/VerifyOTP";
import useAuth from "./components/hooks/useAuth";
import "bootstrap/dist/css/bootstrap.min.css";
import AdminHome from "./components/AdminHomePage/AdminHome";
import ReuseHeaderandBeardcrum from "./components/common/ReuseHeaderandBeardcrum";
import FaqComponent from "./components/AdminHomePage/FaqComponent";
import MsilCallbackWrapper from "./components/Login/MsilCallbackWrapper";
import RoleManagement from "./components/AdminHomePage/RoleManagement";
import "./index.css";
import AddNewService from "./components/AdminHomePage/AddNewService";
import Layout from "../src/components/Login/Layout"; // Import the Layout component

// Unauthorized page
const Unauthorized = () => (
  <div className="container mt-5">
    <h2>Unauthorized Access</h2>
    <p>You do not have permission to view this page.</p>
  </div>
);

// Protected Route Logic with role-based access
const ProtectedRoute = ({ element, allowedRoles }) => {
  const { token, otpVerified, role } = useAuth();

  // CRITICAL FIX: Check sessionStorage/localStorage directly as fallback
  // This prevents redirect loops when state hasn't synced yet on initial render
  const msilToken = localStorage.getItem('msil_id_token');
  const massToken = sessionStorage.getItem('token');
  const currentToken = token || msilToken || massToken;
  const currentRole = role || sessionStorage.getItem('role');

  // Not logged in? Redirect to login.
  if (!currentToken) {
    return <Navigate to="/login" replace />;
  }

  // If the user is a MASS user and OTP is not verified, redirect to OTP.
  // MSIL users do not require OTP, so this check is skipped for them.
  if (currentRole === "MASS" && !otpVerified) {
    return <Navigate to="/verifyOTP" replace />; // Redirect to VerifyOTP page
  }

  // Role not allowed? Redirect to unauthorized page.
  if (currentRole && !allowedRoles.includes(currentRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // All good? Render the protected component.
  return element;
};

// Automatically set body class based on route
const BodyClassSetter = () => {
  const location = useLocation();

  useEffect(() => {
    document.body.className =
      location.pathname.startsWith("/services") ? "services-page" :
      location.pathname.startsWith("/reports") ? "reports-page" : "";

  }, [location.pathname]);

  return null;
};

const App = () => (
  <Router>
    <BodyClassSetter /> {/* Inject class setter for styles */}
    <Routes>
      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="/login" element={<LoginForm />} />
      <Route path="/signup" element={<LoginForm />} />
      <Route path="/health" element={<div>OK</div>} />
      {/* VerifyOTP is specifically for MASS users, but should still be a direct route */}
      <Route path="/verifyOTP" element={<Layout><VerifyOTP /></Layout>} />
      <Route path="/forgot-password" element={<ForgotPasswordModal />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Routes for MASS users */}
      <Route path="/home"
        element={<ProtectedRoute element={<HomePage />} allowedRoles={["MASS_EXECUTIVE","MASS_ADMIN"]} />}
      />
      <Route path="/services"
        element={<ProtectedRoute element={<Service />} allowedRoles={["MASS_EXECUTIVE","MASS_ADMIN"]} />}
      />
      <Route path="/reports"
        element={<ProtectedRoute element={<ReuseHeaderandBeardcrum />} allowedRoles={["MASS_EXECUTIVE","MASS_ADMIN"]} />}
      />

      {/* CRITICAL FIX: Protect all admin routes with authentication and authorization */}
      {/* MSIL OAuth callback route - accessible without token for OAuth flow, but authentication enforced within */}
      <Route path="/AdminHome/:section?" element={<MsilCallbackWrapper />} />
      

      {/* Admin routes - Protected with role-based access control */}
      <Route path="/AddNewService"
        element={<ProtectedRoute element={<AddNewService />} allowedRoles={["MSIL", "SUPER_ADMIN", "ADMIN"]} />}
      />
      <Route path="/AdminHome"
        element={<ProtectedRoute element={<AdminHome />} allowedRoles={["MSIL", "SUPER_ADMIN", "ADMIN"]} />}
      />
      <Route path="/faq" 
        element={<ProtectedRoute element={<FaqComponent />} allowedRoles={["MSIL", "SUPER_ADMIN", "ADMIN"]} />} 
      />
      <Route path="/role-management" 
        element={<ProtectedRoute element={<RoleManagement />} allowedRoles={["SUPER_ADMIN"]} />} 
      />






      <Route path="*" element={<NotFound />} />
    </Routes>
  </Router>
);

export default App;